import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { Redirect } from "@/components/Routing/Redirect";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions, OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface OnboardingUserProfileFormValues {
  avatar?: FormImage[];
  firstName: string;
  lastName: string;
  biography?: string;
  company?: string;
  occupation?: string;
}
export interface OnboardingUserProfileFormProps {
  defaultValues: OnboardingUserProfileFormValues;
  isCompanyProject: boolean;
  isSubmitting: boolean;
  onSubmit: (data: OnboardingUserProfileFormValues) => Promise<void>;
}
export function OnboardingUserProfileForm({
  defaultValues,
  isCompanyProject,
  onSubmit,
  isSubmitting,
}: OnboardingUserProfileFormProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const formMethods = useForm<OnboardingUserProfileFormValues>({
    defaultValues,
  });
  const sessionUser = useSessionUser();

  if (!sessionUser.codeOfConductAgreedAt) {
    return <Redirect to={({ slug: currSlug }) => routes.onboardingFlow.codeOfConduct({ slug: currSlug })} />;
  }

  function handleSubmit() {
    const data = formMethods.getValues();

    void onSubmit({
      avatar: data.avatar,
      firstName: data.firstName,
      lastName: data.lastName,
      biography: data.biography,
      occupation: data.occupation,
    });
  }

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <OnboardingFlowCard>
        <Stack gap="sm">
          <span className="text-heading-3">{t("page.onboarding.user-details.title.1")}</span>
          <span className="text-heading-3">{t("page.onboarding.user-details.title.2")}</span>
        </Stack>
        <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
          <Form.Body>
            <FormField label={t("page.onboarding.user-details.form.avatar.label")} required>
              <section className="flex size-full items-center justify-center">
                <FormImageInput<OnboardingUserProfileFormValues, "avatar">
                  isAvatarInput
                  cropper
                  name="avatar"
                  rules={{
                    validate: {
                      size: createImageSizeRule({ t }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                />
              </section>
            </FormField>
            <FormField label={t("page.onboarding.user-details.form.first-name.label")} required>
              <FormInput<OnboardingUserProfileFormValues>
                name="firstName"
                placeholder={t("page.onboarding.user-details.form.first-name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.onboarding.user-details.form.first-name.label"),
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.onboarding.user-details.form.last-name.label")} required>
              <FormInput<OnboardingUserProfileFormValues>
                name="lastName"
                placeholder={t("page.onboarding.user-details.form.last-name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.onboarding.user-details.form.last-name.label"),
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.onboarding.user-details.form.biography.label")}>
              <FormTextArea<OnboardingUserProfileFormValues>
                name="biography"
                placeholder={t("page.onboarding.user-details.form.biography.placeholder")}
              />
            </FormField>
            {isCompanyProject && (
              <>
                <FormField label={t("page.onboarding.user-details.form.company.label")}>
                  <FormInput<OnboardingUserProfileFormValues> name="company" disabled />
                </FormField>
                <FormField label={t("page.onboarding.user-details.form.occupation.label")}>
                  <FormInput<OnboardingUserProfileFormValues>
                    name="occupation"
                    placeholder={t("page.onboarding.user-details.form.occupation.placeholder")}
                  />
                </FormField>
              </>
            )}
          </Form.Body>
          <OnboardingFlowActions>
            <Stack gap="sm">
              <Button fullWidth isLoading={isSubmitting} type="submit" data-testid="next-step-button">
                {t("common.action.next")}
              </Button>
              <Button
                type="link"
                variant="soft"
                disabled={isSubmitting}
                href={routes.onboardingFlow.slides({ slug })}
                fullWidth
              >
                {t("common.action.back")}
              </Button>
            </Stack>
          </OnboardingFlowActions>
        </Form.Root>
      </OnboardingFlowCard>
    </OnboardingFlowRoot>
  );
}
